import styled from 'styled-components';
import { GlobalToken } from 'antd';

import { EngineConfigStatus } from '@/store/Engine/constants';
import { StageCard } from '@/comp/StationStages/styled';

interface EngineVersionContainerProps {
  $configurationStatus: string;
  theme: {
    token: GlobalToken;
  };
}

export const EngineVersionContainer = styled.div<EngineVersionContainerProps>`
  display: flex;
  flex-direction: column;

  .engine-version-one-alert-container {
    display: inline-block;
    margin-bottom: ${({ theme: { token } }) => token.marginXS}px;
  }

  .engine-tabs-container {
    background-color: ${({ theme: { token } }) => token.colorBgContainer};

    &.recommendation-container {
      .engine-version-tabs {
        .ant-tabs-extra-content {
          .created-on-label {
            color: ${({ theme: { token } }) => token.colorBorder};
            margin-left: ${({ theme: { token } }) => token.marginXS}px;
          }

          .approval-report {
            text-decoration: underline;
            margin-right: ${({ theme: { token } }) => token.marginXS}px;
          }
        }
      }
    }
  }

  .kpi-container {
      margin-left: ${({ theme: { token } }) => token.marginMD}px;
      margin-top: ${({ theme: { token } }) => token.marginMD}px;
  }

  .engine-version-tabs {
    background-color: ${({ theme: { token } }) => token.colorBgContainer};
  }

  .ant-tabs-content {
    overflow: scroll;
    height: calc(
      100vh -
        ${({ $configurationStatus, theme: { token } }) => {
          const baseHeight = $configurationStatus === EngineConfigStatus.Recommendation 
            ? token.paddingXL * 7 + token.marginLG * 3 
            : token.paddingXL * 6 + token.marginLG * 3;
          return `${baseHeight}px`;
        }}
    );
  }

  /* Reduce tabs content height when version alert is visible */
  &.has-version-alert .ant-tabs-content {
    height: calc(
      100vh -
        ${({ $configurationStatus, theme: { token } }) => {
          const baseHeight = $configurationStatus === EngineConfigStatus.Recommendation 
            ? token.paddingXL * 7 + token.marginLG * 2 
            : token.paddingXL * 6 + token.marginLG * 2;
          const warningAdjustment = token.paddingXL + token.marginLG;
          return `${baseHeight + warningAdjustment}px`;
        }}
    );
  }

  .ant-tabs-nav {
    margin: 0 !important;
  }

  .footer-publish-buttons {
    align-self: end;
    margin-top: ${({ theme: { token } }) => token.marginXS}px;

    .footer-publish-button {
      margin-left: ${({ theme: { token } }) => token.marginXS}px;
    }
  }

`;

interface EnginePageContainerProps {
  $hasUpdatePermission: boolean;
  $activeTab: string;
  theme: {
    token: GlobalToken;
  };
}

export const EnginePageContainer = styled.div<EnginePageContainerProps>`
  padding: ${({ theme: { token } }) => `${token.paddingMD}px`};
  flex-grow: 1;
  position: relative;
  overflow: hidden;

  /* hide number input arrows - Chrome, Safari, Edge, Opera */

  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  /* hide number input arrows - Firefox */

  input[type='number'] {
    -moz-appearance: textfield;
  }

  .eco-select-station {
    width: 310px;
  }

  .recommendation-tab {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
  }

  .engine-beta-tag {
    margin-left: ${({ theme: { token } }) => token.marginXXS}px;
  }

  .engine-header-container {
    display: flex;
    align-items: center;
    gap: 1rem;

    @media (max-width: 1024px) {
      flex-direction: column-reverse;
      align-items: stretch;
      gap: 1.5rem;
    }

    ${StageCard} {
      margin: 0;
      flex: 1;
    }

    .eco-select-station {
      @media (max-width: 1024px) {
        width: 100%;
      }
    }
  }

  .ant-tabs {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    height: 100%;
    margin-top: ${({ theme: { token } }) => token.marginXS}px;

    .ant-tabs-content {
      .ant-tabs-tabpane {
        height: 100%;
      }
    }

    .ant-tabs-nav {
      margin: 0 auto ${({ theme: { token } }) => token.marginMD}px;
      min-width: 800px;

      .ant-tabs-nav-wrap {
        display: flex;
        justify-content: center;

        .ant-tabs-tab {
          padding: 0 ${({ theme: { token } }) => token.paddingXS}px;
          margin: 0 ${({ theme: { token } }) => token.marginXS}px ${({ theme: { token } }) => token.marginXXS}px
            ${({ theme: { token } }) => token.marginXS}px;
          flex-grow: 0;

          .disabled-tab {
            cursor: default;
            pointer-events: none;
            color: ${({ theme: { token } }) => token.colorBorder};
          }

          .ant-tabs-tab-btn {
            ${props => !props.$hasUpdatePermission && 'cursor: no-drop;'}
            .disabled {
              cursor: no-drop;
              color: ${({ theme: { token } }) => token.colorBorder};
            }
          }

          &.ant-tabs-tab-active {
            .ant-tabs-tab-btn {
              .disabled {
                cursor: pointer;
                color: ${({ theme: { token } }) => token.colorText};
              }
            }
          }
        }

        .ant-tabs-ink-bar {
          background-color: ${({ theme: { token } }) => token.colorPrimary};
          height: 3px;
          border-radius: 5px 5px 0 0;
        }
      }

      .bold-text {
        font-weight: ${({ theme: { token } }) => token.fontWeightStrong};
      }
    }
  }

  .new-recommendation-indication {
    color: ${({ theme: { token } }) => token.colorError};
  }
`;
